import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import feathers from '@feathersjs/feathers';
import rest from '@feathersjs/rest-client';
import { ReplaySubject, BehaviorSubject } from 'rxjs';
import { IAccountType } from '../constants/account-types';
import { analyzeAndValidateNgModules } from '@angular/compiler';
import { API_ADDRESS_LOCAL, API_ADDRESS_PROD } from './api-addr/l';
import { HttpClient, HttpHeaders } from '@angular/common/http';
const auth = require('@feathersjs/authentication-client');

export const F_ERR_MSG__FAILED_TO_FETCH = 'Failed to fetch'; // if feather is unable to connect to the server
export const F_ERR_MSG__INVALID_LOGIN = 'Invalid login'; 
export const F_ERR_MSG__REAUTH_NO_TOKEN = 'No accessToken found in storage'; 

const TOKEN_REFRESH_INTERVAL = 10*60*1000;

interface IAuthRes {
  accessToken: string,
  user?: IUserInfoCore, 
  authentication?: {
    payload: IUserInfoCore
  }
}
interface IApiFindQuery {
  query?: {
    $limit?: number,
    [key: string]: any;
  }
  [key: string]: any;
}
interface IUploadResponse { 
  success: boolean, 
  filePath: string,
}
export interface IUserInfoCore {
  email: string,
  uid: number,
  accountType: IAccountType,
  accountId: number,
  accountInfo: {
    institutionId? :number // test admins only
  },
  firstName: string,
  lastName: string,
}

export interface IUserInfo extends IUserInfoCore{
  accessToken: string,
}

export const DB_TIMEZONE = 'Z';

const getApiAddress = () => {
  return API_ADDRESS_PROD;
  // return API_ADDRESS_LOCAL;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private api = <any>feathers();
  private restClient = <any>rest(getApiAddress());
  private _user:IUserInfo;
  private userSub:BehaviorSubject<IUserInfo> = new BehaviorSubject(null);
  private reauthCompletedSub:BehaviorSubject<boolean> = new BehaviorSubject(false);
  private apiNetFail:BehaviorSubject<boolean> = new BehaviorSubject(false); // gets overridden upon registration

  constructor(
    private router: Router,
    private httpClient:HttpClient,
  ) { 
    this.api.configure(this.restClient.fetch(window['fetch']));
    this.api.configure(auth({ storage: window['localStorage'] }));
    this.reauth();
    // setInterval(this.refreshToken, TOKEN_REFRESH_INTERVAL);
    // this.clearLocalSession()
  }

  public user(){
    return this.userSub;
  }

  getTimezone(){
    return 'America/Toronto';
  }

  public getReauthCompletedSub(){
    return this.reauthCompletedSub;
  }

  public registerNoApiNetSub(apiNetFail:BehaviorSubject<boolean>){
    apiNetFail.next(this.apiNetFail.getValue()); // usually this first one will only be around for a fraction of a second. in any case, it is private so nothing outside of this calss should be subscribing to it becaus it will be wiped out
    this.apiNetFail = apiNetFail;
  }
  private clearNetworkError = (res:any) => {
    this.apiNetFail.next(false);
    return res;
  }
  private catchNetworkError = (e) => {
    // console.log('catch net error', e.message)
    if (e.message === F_ERR_MSG__FAILED_TO_FETCH){
      this.apiNetFail.next(true);
    }
    throw e;
  }

  private clearLocalSession(){
    
    this.api.authentication.removeAccessToken();
  }

  private refreshUserInfo = (res:IAuthRes, isSilent:boolean=false) => {
    this._user = {
      ... res.user || res.authentication.payload,
      accessToken:  res.accessToken,
    }
    if (!isSilent){
      this.userSub.next(this._user)
    }
    return res;
  }
  

  uploadFile(file: File, purpose:string='_general') : Promise<IUploadResponse>{
    const formData: FormData = new FormData();
    formData.append('form_upload', file, file.name);
    formData.append('uid', ''+this._user.uid);
    formData.append('purpose', purpose);
    formData.append('jwt', this._user.accessToken);
    return this.httpClient
      .post(getApiAddress()+'/upload', formData)
      .toPromise()
      .then(res => <IUploadResponse>res );
  }

  public refreshToken = () =>{
    // if (this._user && !this.isLoggingIn){
    //   this.apiFind('/rest/auth/refresh-token', {})
    //     .then(refreshed => {
    //       this.api.authentication.setAccessToken(refreshed[0]);
    //       // setTimeout(()=>{
    //       //   console.log(['refreshToken', refreshed[0], this.api.authentication.getAccessToken()])
    //       // }, 1000)
    //     })
    //     .catch(()=>{
    //       console.log('soft fail')
    //       return this.api
    //         .reAuthenticate(true)
    //         .then(res => this.refreshUserInfo(res, true))
    //     })
    //     .catch(e =>{
    //       console.log('hard fail')
    //       return this.clearUser(e);
    //     }
    // }
  }

  clearUser = (e?:any) => {
    this.userSub.next(null);
    this._user = null;
  }

  public reauth() : Promise<any> {
    return this.api
      .reAuthenticate()
      .then(this.clearNetworkError)
      .then(this.refreshUserInfo)
      .then( res => { this.reauthCompletedSub.next(true); return res })
      .catch( e => { this.reauthCompletedSub.next(true); return e })
      .catch(this.catchNetworkError)
      .catch( e => { if (e.message !== F_ERR_MSG__REAUTH_NO_TOKEN){ throw e;} }) // no token is not really an error since we are not checking for it in the first place
  }

  isLoggingIn:boolean;
  public login(email:string, password:string) : Promise<any> {
    this.isLoggingIn = true;
    return this.api
      .authenticate({
        strategy: 'local',
        email,
        password,
      })
      .then(this.clearNetworkError)
      .then(this.refreshUserInfo)
      .then(()=> this.isLoggingIn = false)
      .catch(this.catchNetworkError)
      .catch(()=> this.isLoggingIn = false)
  }

  public logout() : Promise<any> {
    return this.api
      .logout()
      .then(this.clearNetworkError)
      .then(this.clearUser())
      .catch(e => { this.clearLocalSession(); throw e; })
      .catch(this.catchNetworkError)
  }

  public apiGet(route:string, id:string | number) : Promise<any> {
    return this.api
      .service(route)
      .get(id)
      .then(this.clearNetworkError)
      .catch(this.catchNetworkError)
  }

  public apiFind(route:string, queryOptions:IApiFindQuery) : Promise<any> {
    return this.api
      .service(route)
      .find(queryOptions)
      .then(this.clearNetworkError)
      .catch(this.catchNetworkError)
  }
  public apiCreate(route:string, data:any) : Promise<any> {
    return this.api
      .service(route)
      .create(data)
      .then(this.clearNetworkError)
      .catch(this.catchNetworkError)
  }
  public apiPatch(route:string, id:string | number, data:any) : Promise<any> {
    // console.log('apiPatch', id, data)
    return this.api
      .service(route)
      .patch(id, data)
      .then(this.clearNetworkError)
      .catch(this.catchNetworkError)
  }
  public apiRemove(route:string, id:string) : Promise<any> {
    return this.api
      .service(route)
      .remove(id)
      .then(this.clearNetworkError)
      .catch(this.catchNetworkError)
  }

}
